import { createDefaultProjectionModel } from "./defaults";
import { clamp, roundClients } from "./format";
import type {
  AdoptionAssumptions,
  AlternativeComparisonRow,
  BreakEvenAnalysis,
  Clinic,
  ConnectionPack,
  CostBreakdown,
  CostItem,
  MarketingPack,
  MixWeight,
  MonthProjection,
  ProjectionAlternative,
  ProjectionModel,
  ProjectionWorkspace,
  ScenarioResult,
  SimulationResult,
} from "./types";

export function toMonthlyArs(item: CostItem, usdArs: number): number {
  const fx = item.currency === "USD" ? usdArs : 1;
  const monthly = item.frequency === "annual" ? item.amount / 12 : item.amount;
  return monthly * fx;
}

export function whatsappUnitCost(model: ProjectionModel): number {
  return model.assumptions.usdArs * model.assumptions.metaUsdPerWhatsapp;
}

export function emailUnitCost(model: ProjectionModel): number {
  const emails = model.assumptions.sendgridEmailsPerMonth;
  if (emails <= 0) return 0;
  return (model.assumptions.sendgridUsdPerMonth * model.assumptions.usdArs) / emails;
}

export function summarizeCosts(model: ProjectionModel, includeDebt: boolean): CostBreakdown {
  const items = model.costs.map((item) => {
    const monthlyArs = toMonthlyArs(item, model.assumptions.usdArs);
    const included = item.enabled && (item.kind !== "debt" || includeDebt);
    return {
      id: item.id,
      name: item.name,
      category: item.category,
      kind: item.kind,
      monthlyArs,
      included,
      enabled: item.enabled,
    };
  });

  const operating = items
    .filter((item) => item.kind === "operating" && item.included)
    .reduce((sum, item) => sum + item.monthlyArs, 0);
  const debt = items
    .filter((item) => item.kind === "debt" && item.enabled)
    .reduce((sum, item) => sum + item.monthlyArs, 0);
  const included = items
    .filter((item) => item.included)
    .reduce((sum, item) => sum + item.monthlyArs, 0);

  const categoryMap = new Map<string, number>();
  items.filter((item) => item.included).forEach((item) => {
    categoryMap.set(item.category, (categoryMap.get(item.category) ?? 0) + item.monthlyArs);
  });

  return {
    monthlyArs: items.reduce((sum, item) => sum + (item.enabled ? item.monthlyArs : 0), 0),
    operating,
    debt,
    included,
    byCategory: [...categoryMap.entries()]
      .map(([category, amount]) => ({ category, amount }))
      .sort((a, b) => b.amount - a.amount),
    items,
  };
}

export function clinicBasePrice(clinic: Pick<Clinic, "professionals" | "consultorios">, model: ProjectionModel): number {
  const extraProf = Math.max(0, clinic.professionals - 1) * model.plans.extraProfessional;
  const extraConsult = Math.max(0, clinic.consultorios - 1) * model.plans.extraConsultorio;
  return model.plans.basePrice + extraProf + extraConsult;
}

export function panelPrice(clinicCount: number, model: ProjectionModel): number {
  const extra = Math.max(0, clinicCount - model.panelClinicas.includedClinics);
  return model.panelClinicas.basePrice + extra * model.panelClinicas.extraClinicPrice;
}

function enabledClinics(model: ProjectionModel): Clinic[] {
  return model.clinics.filter((clinic) => clinic.enabled);
}

export function portfolioClinicCount(model: ProjectionModel): number {
  if (model.portfolioMode === "synthetic") {
    return Math.max(0, model.synthetic.clinicCount);
  }
  return enabledClinics(model).length;
}

export function portfolioBaseScaleMrr(model: ProjectionModel): number {
  if (model.portfolioMode === "synthetic") {
    const count = portfolioClinicCount(model);
    return count * clinicBasePrice({
      professionals: model.synthetic.avgProfessionals,
      consultorios: model.synthetic.avgConsultorios,
    }, model);
  }
  return enabledClinics(model).reduce((sum, clinic) => sum + clinicBasePrice(clinic, model), 0);
}

function normalizeMix<T extends { id: string; enabled: boolean }>(
  packs: T[],
  mix: MixWeight[],
): Array<{ pack: T; weight: number }> {
  const enabled = packs.filter((pack) => pack.enabled);
  const rows = enabled.map((pack) => {
    const configured = mix.find((item) => item.packId === pack.id)?.weight ?? 0;
    return { pack, weight: Math.max(0, configured) };
  });
  const total = rows.reduce((sum, row) => sum + row.weight, 0);
  if (total <= 0) {
    const even = rows.length > 0 ? 1 / rows.length : 0;
    return rows.map((row) => ({ ...row, weight: even }));
  }
  return rows.map((row) => ({ ...row, weight: row.weight / total }));
}

function weightedAverage<T>(rows: Array<{ pack: T; weight: number }>, pick: (pack: T) => number): number {
  return rows.reduce((sum, row) => sum + row.weight * pick(row.pack), 0);
}

export function simulate(model: ProjectionModel, adoption: AdoptionAssumptions = model.adoption): SimulationResult {
  const clinics = portfolioClinicCount(model);
  const baseScaleMrr = portfolioBaseScaleMrr(model);
  const avanzadoDelta = Math.max(0, model.plans.avanzadoPrice - model.plans.basePrice);
  const avanzadoClients = roundClients(clinics, adoption.pctAvanzado);
  const avanzadoMrr = avanzadoClients * avanzadoDelta;

  const conexionClients = roundClients(clinics, adoption.pctConexion);
  const connectionMix = normalizeMix(model.connectionPacks, adoption.connectionMix);
  const ticketConexion = weightedAverage(connectionMix, (pack: ConnectionPack) => pack.price);
  const weightedWaQuota = weightedAverage(connectionMix, (pack: ConnectionPack) => pack.quota);
  const conexionMrr = conexionClients * ticketConexion;
  const waUnit = whatsappUnitCost(model);
  const metaCost = conexionClients * weightedWaQuota * clamp(adoption.whatsappUsageRate, 0, 2) * waUnit;

  const marketingClients = roundClients(clinics, adoption.pctMarketing);
  const marketingMix = normalizeMix(model.marketingPacks, adoption.marketingMix);
  const ticketMarketing = weightedAverage(marketingMix, (pack: MarketingPack) => pack.price);
  const weightedEmailQuota = weightedAverage(marketingMix, (pack: MarketingPack) => pack.emailsPerMonth);
  const marketingMrr = marketingClients * ticketMarketing;
  const mailUnit = emailUnitCost(model);
  const emailCost = marketingClients * weightedEmailQuota * clamp(adoption.emailUsageRate, 0, 2) * mailUnit;

  const panelClients = roundClients(clinics, adoption.pctPanel);
  const panelMrr = panelClients * panelPrice(Math.max(1, adoption.panelAvgClinics), model);

  const mrrTotal = baseScaleMrr + avanzadoMrr + conexionMrr + marketingMrr + panelMrr;
  const variableCost = metaCost + emailCost;
  const costs = summarizeCosts(model, adoption.includeDebt);
  const resultado = mrrTotal - costs.included - variableCost;
  const arpu = clinics > 0 ? mrrTotal / clinics : 0;
  const contributionPerClinic = clinics > 0 ? (mrrTotal - variableCost) / clinics : 0;

  return {
    clinics,
    avanzadoClients,
    conexionClients,
    marketingClients,
    panelClients,
    baseScaleMrr,
    avanzadoMrr,
    conexionMrr,
    marketingMrr,
    panelMrr,
    mrrTotal,
    ticketConexion,
    ticketMarketing,
    weightedWaQuota,
    weightedEmailQuota,
    metaCost,
    emailCost,
    variableCost,
    fixedOperating: costs.operating,
    fixedDebt: costs.debt,
    fixedTotal: costs.included,
    resultado,
    resultadoPerClinic: clinics > 0 ? resultado / clinics : 0,
    arpu,
    contributionPerClinic,
    metaOverMrr: mrrTotal > 0 ? metaCost / mrrTotal : 0,
    variableOverMrr: mrrTotal > 0 ? variableCost / mrrTotal : 0,
    marginPct: mrrTotal > 0 ? resultado / mrrTotal : 0,
    breakEvenReached: resultado >= 0,
    costPerClinic: clinics > 0 ? costs.included / clinics : 0,
    whatsappUnitCost: waUnit,
    emailUnitCost: mailUnit,
  };
}

export function analyzeBreakEven(result: SimulationResult): BreakEvenAnalysis {
  const contribution = result.contributionPerClinic;
  const reachable = contribution > 0;
  const clinicsNeeded = reachable ? Math.ceil(result.fixedTotal / contribution) : Number.POSITIVE_INFINITY;
  const arpuNeeded = result.clinics > 0
    ? (result.fixedTotal + result.variableCost) / result.clinics
    : result.fixedTotal;
  return {
    reachable,
    clinicsNeeded,
    arpuNeeded,
    gap: Math.max(0, -result.resultado),
    contributionPerClinic: contribution,
  };
}

function portfolioAverages(model: ProjectionModel): { professionals: number; consultorios: number } {
  if (model.portfolioMode === "synthetic") {
    return {
      professionals: model.synthetic.avgProfessionals,
      consultorios: model.synthetic.avgConsultorios,
    };
  }
  const clinics = enabledClinics(model);
  if (clinics.length === 0) {
    return { professionals: 1, consultorios: 1 };
  }
  return {
    professionals: clinics.reduce((sum, clinic) => sum + clinic.professionals, 0) / clinics.length,
    consultorios: clinics.reduce((sum, clinic) => sum + clinic.consultorios, 0) / clinics.length,
  };
}

export function projectMonths(model: ProjectionModel): MonthProjection[] {
  const months = Math.max(1, Math.min(60, Math.round(model.growth.months)));
  const start = portfolioAverages(model);
  let clinics = portfolioClinicCount(model);
  let professionalsTotal = start.professionals * clinics;
  let consultoriosTotal = start.consultorios * clinics;
  const rows: MonthProjection[] = [];
  let cumulative = 0;

  for (let month = 1; month <= months; month += 1) {
    if (month > 1) {
      const keepRatio = 1 - clamp(model.growth.churnMonthlyPct, 0, 1);
      professionalsTotal *= keepRatio;
      consultoriosTotal *= keepRatio;
      const remaining = clinics * keepRatio;
      const newcomers = Math.max(0, model.growth.newClinicsPerMonth);
      professionalsTotal += newcomers * model.growth.avgNewProfessionals;
      consultoriosTotal += newcomers * model.growth.avgNewConsultorios;
      clinics = remaining + newcomers;
    }

    const simulated = simulate({
      ...model,
      portfolioMode: "synthetic",
      synthetic: {
        clinicCount: clinics,
        avgProfessionals: clinics > 0 ? professionalsTotal / clinics : model.growth.avgNewProfessionals,
        avgConsultorios: clinics > 0 ? consultoriosTotal / clinics : model.growth.avgNewConsultorios,
      },
    });
    cumulative += simulated.resultado;
    rows.push({
      month,
      clinics: simulated.clinics,
      mrrTotal: simulated.mrrTotal,
      fixedTotal: simulated.fixedTotal,
      variableCost: simulated.variableCost,
      resultado: simulated.resultado,
      arpu: simulated.arpu,
      cumulativeResult: cumulative,
    });
  }

  return rows;
}

export function simulateScenarios(model: ProjectionModel): ScenarioResult[] {
  return model.scenarios.map((scenario) => ({
    scenario,
    result: simulate(model, scenario.adoption),
  }));
}

export function suggestedConnectionPack(volume: number, packs: ConnectionPack[]): ConnectionPack | null {
  const enabled = packs.filter((pack) => pack.enabled).sort((a, b) => a.quota - b.quota);
  if (enabled.length === 0) return null;
  return enabled.find((pack) => pack.quota >= volume) ?? enabled[enabled.length - 1];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

export function parseProjectionModel(raw: unknown): ProjectionModel | null {
  if (!isRecord(raw) || raw.version !== 1) return null;
  const fallback = createDefaultProjectionModel();
  try {
    const model = raw as unknown as ProjectionModel;
    if (!Array.isArray(model.costs) || !Array.isArray(model.clinics)) return null;
    return {
      ...fallback,
      ...model,
      version: 1,
      assumptions: { ...fallback.assumptions, ...model.assumptions },
      plans: { ...fallback.plans, ...model.plans },
      panelClinicas: { ...fallback.panelClinicas, ...model.panelClinicas },
      synthetic: { ...fallback.synthetic, ...model.synthetic },
      adoption: {
        ...fallback.adoption,
        ...model.adoption,
        connectionMix: Array.isArray(model.adoption?.connectionMix)
          ? model.adoption.connectionMix
          : fallback.adoption.connectionMix,
        marketingMix: Array.isArray(model.adoption?.marketingMix)
          ? model.adoption.marketingMix
          : fallback.adoption.marketingMix,
      },
      growth: { ...fallback.growth, ...model.growth },
      costs: model.costs,
      connectionPacks: Array.isArray(model.connectionPacks) ? model.connectionPacks : fallback.connectionPacks,
      marketingPacks: Array.isArray(model.marketingPacks) ? model.marketingPacks : fallback.marketingPacks,
      clinics: model.clinics,
      scenarios: Array.isArray(model.scenarios) ? model.scenarios : fallback.scenarios,
      portfolioMode: model.portfolioMode === "synthetic" ? "synthetic" : "detailed",
    };
  } catch {
    return null;
  }
}

export function emptyCostItem(): CostItem {
  return {
    id: `cost_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`,
    name: "Nuevo gasto",
    amount: 0,
    currency: "ARS",
    frequency: "monthly",
    category: "General",
    kind: "operating",
    enabled: true,
  };
}

export function cloneProjectionModel(model: ProjectionModel): ProjectionModel {
  return JSON.parse(JSON.stringify(model)) as ProjectionModel;
}

export function modelsEqual(left: ProjectionModel, right: ProjectionModel): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

export function nextAlternativeName(existing: ProjectionAlternative[]): string {
  const used = new Set(existing.map((item) => item.name.trim().toLowerCase()));
  let index = existing.length + 1;
  let name = `Alternativa ${index}`;
  while (used.has(name.toLowerCase())) {
    index += 1;
    name = `Alternativa ${index}`;
  }
  return name;
}

function parseAlternative(raw: unknown): ProjectionAlternative | null {
  if (!isRecord(raw)) return null;
  const model = parseProjectionModel(raw.model);
  if (!model) return null;
  const id = typeof raw.id === "string" && raw.id ? raw.id : `alt_${Date.now().toString(36)}`;
  const name = typeof raw.name === "string" && raw.name.trim() ? raw.name.trim() : "Alternativa";
  return {
    id,
    name,
    notes: typeof raw.notes === "string" ? raw.notes : "",
    savedAt: typeof raw.savedAt === "string" ? raw.savedAt : new Date().toISOString(),
    model,
  };
}

export function parseProjectionWorkspace(raw: unknown): ProjectionWorkspace | null {
  if (!isRecord(raw)) return null;
  if (isRecord(raw.draft)) {
    const draft = parseProjectionModel(raw.draft);
    if (!draft) return null;
    const alternatives = Array.isArray(raw.alternatives)
      ? raw.alternatives
          .map(parseAlternative)
          .filter((item): item is ProjectionAlternative => item !== null)
      : [];
    const currentId = typeof raw.currentId === "string" ? raw.currentId : null;
    return {
      version: 1,
      currentId: currentId && alternatives.some((item) => item.id === currentId) ? currentId : null,
      draft,
      alternatives,
    };
  }
  const draft = parseProjectionModel(raw);
  if (!draft) return null;
  return {
    version: 1,
    currentId: null,
    draft,
    alternatives: [],
  };
}

export function createDefaultWorkspace(): ProjectionWorkspace {
  return {
    version: 1,
    currentId: null,
    draft: createDefaultProjectionModel(),
    alternatives: [],
  };
}

export function compareAlternatives(
  workspace: ProjectionWorkspace,
  includeDraft: boolean,
): AlternativeComparisonRow[] {
  const rows: AlternativeComparisonRow[] = workspace.alternatives.map((item) => ({
    id: item.id,
    name: item.name,
    isDraft: false,
    isCurrent: item.id === workspace.currentId,
    savedAt: item.savedAt,
    notes: item.notes,
    result: simulate(item.model),
  }));
  if (includeDraft) {
    rows.unshift({
      id: "draft",
      name: workspace.currentId
        ? `${workspace.alternatives.find((item) => item.id === workspace.currentId)?.name ?? "Actual"} (edición)`
        : "Borrador actual",
      isDraft: true,
      isCurrent: true,
      savedAt: null,
      notes: "",
      result: simulate(workspace.draft),
    });
  }
  return rows;
}

export function bestIds(
  rows: AlternativeComparisonRow[],
  metric: (result: SimulationResult) => number,
  direction: "max" | "min" = "max",
): Set<string> {
  if (rows.length === 0) return new Set();
  const scored = rows.map((row) => ({ id: row.id, value: metric(row.result) }));
  const target = direction === "max"
    ? Math.max(...scored.map((item) => item.value))
    : Math.min(...scored.map((item) => item.value));
  return new Set(scored.filter((item) => item.value === target).map((item) => item.id));
}
